import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import BoxedContent from 'ui/molecules/boxed-content';
import useApiCall from 'hooks/use-api-call';
import { AdminApi, AdminInvitation, DistributionPlatformTypeEnum } from 'api';
import LoadingRing from 'ui/atoms/loading-ring';
import Translate from 'ui/atoms/translate';
import useClipboard from 'ui/hooks/use-clipboard';
import { compact } from 'lodash';
import ProductInvitationExtendedDetailsCard from 'apps/issuer/pages/product-details/invitations/invitation-details/product-invitation-card';
import NoInvestmentDocumentsInvitationModal from './no-investment-documents-modal';
import Hint from 'src/ui/atoms/hint';
import { hasDistributionViewDataPermission } from 'src/apps/issuer/helpers/distributionPlatformPermissions';

const mapInvestmentInvitationStatus = (hasInvestment?: boolean) => {
  if (hasInvestment) return 'committed';
  return 'new';
};

const BoxedIssuerInvestmentInvitation: FunctionComponent<
  AdminInvitation & {
    onDeactivated?: ({ productId }: { productId: string }) => void;
    issuersCanManage?: boolean;
  }
> = (props) => {
  const { issuersCanManage, onDeactivated = () => {}, ...invitationProps } = props;
  const [invitation, setInvitation] = useState<
    AdminInvitation & {
      email?: string;
      name?: string;
      invitationLink?: string;
    }
  >(invitationProps);
  const [noInvestmentDocumentsModalOpen, setNoInvestmentDocumentsModalOpen] = useState(false);

  const {
    id,
    distributionPlatformDetails,
    maxNumberOfTokens,
    pricePerToken,
    country,
    email,
    token,
    ruleType,
    name,
    investment,
    createdAt,
    signedPercentage,
    invitationLink,
    distributionInformation,
  } = invitation;

  const { copied, copy } = useClipboard();

  useEffect(() => {
    const { issuersCanManage, ...invitationProps } = props;
    setInvitation(invitationProps);
  }, [props]);

  const { withApi, makeAuthenticatedApi, loading } = useApiCall(false);

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const deactivate = useCallback(() => {
    withApi(async () => {
      if (id) {
        await api.adminInvitationsDestroy({ id });
        onDeactivated({ productId: token?.id });
      }
    });
  }, [withApi, api, id, onDeactivated]);

  const copyButtonAction = useCallback(() => {
    return !distributionPlatformDetails.usesEffecta || distributionInformation?.investmentDocuments?.length
      ? copy(invitationLink || '')
      : setNoInvestmentDocumentsModalOpen(true);
  }, [distributionInformation?.investmentDocuments, invitationLink]);

  return (
    <>
      <BoxedContent
        releaseSpaceWhenTitleIsEmpty={true}
        title={<Translate name="common.generalInformation" />}
        actionButtons={compact([
          issuersCanManage && {
            name: 'deactivate',
            content: <Translate name="investmentInvitation.deactivate" />,
            variant: 'link',
            disabled: !!investment,
            loading,
            onClick: deactivate,
          },
          hasDistributionViewDataPermission([distributionPlatformDetails]) &&
            distributionPlatformDetails.platformType === DistributionPlatformTypeEnum.FRONTEND && {
              name: 'copy',
              disabled: copied,
              content: <Translate name={`copy.link.${copied ? 'copied' : 'copy'}`} />,
              variant: 'primary',
              onClick: copyButtonAction,
            },
        ])}
        extraInfo={
          !distributionInformation &&
          distributionPlatformDetails.usesEffecta && (
            <Hint variant="warning">
              <Translate name="investmentInvitation.noDistributionInformation" />
            </Hint>
          )
        }
      >
        {loading && <LoadingRing />}
        {!loading && (
          <ProductInvitationExtendedDetailsCard
            id={id}
            distributionPlatform={distributionPlatformDetails.displayName}
            maxNumberOfTokens={maxNumberOfTokens}
            pricePerToken={pricePerToken}
            country={country as any}
            email={email}
            status={mapInvestmentInvitationStatus(!!investment)}
            ruleType={ruleType}
            token={token}
            name={name}
            signedPercentage={signedPercentage}
            createdAt={createdAt}
          />
        )}
      </BoxedContent>
      {noInvestmentDocumentsModalOpen && (
        <NoInvestmentDocumentsInvitationModal
          invitationLink={invitationLink || ''}
          onClose={() => setNoInvestmentDocumentsModalOpen(false)}
        />
      )}
    </>
  );
};

export default BoxedIssuerInvestmentInvitation;
